/**
 * Pure validation + publish-decisions helpers for the form editor page.
 * No React, no Supabase — these run synchronously over the in-memory
 * FormDefinition + diff state.
 */
import type { FormDefinition, ImpactResult } from '../api/form-builder-api'
import type { ClassifiedChange } from '../lib/classify-changes'
import { diffItemId } from '../lib/diff-engine'

export interface ValidationError {
  field_key?: string
  rule_id?: string
  message: string
}

export function validateDefinition(def: FormDefinition): ValidationError[] {
  const errors: ValidationError[] = []
  const seenKeys = new Set<string>()

  for (const field of def.fields) {
    if (!field.field_key || !field.field_key.match(/^[a-z][a-z0-9_]*$/)) {
      errors.push({
        field_key: field.field_key,
        message: `کلید فیلد «${field.field_key}» نامعتبر است (فقط حروف کوچک، اعداد، و _ مجاز است)`,
      })
    }
    if (seenKeys.has(field.field_key)) {
      errors.push({
        field_key: field.field_key,
        message: `کلید فیلد «${field.field_key}» تکراری است`,
      })
    }
    seenKeys.add(field.field_key)

    if (field.field_type === 'repeatable_array') {
      const subs = (field.type_config.sub_fields as Array<{ field_key: string }> | undefined) ?? []
      const subKeys = new Set<string>()
      for (const sf of subs) {
        if (subKeys.has(sf.field_key)) {
          errors.push({
            field_key: field.field_key,
            message: `کلید زیر‌فیلد «${sf.field_key}» در آرایه «${field.field_key}» تکراری است`,
          })
        }
        subKeys.add(sf.field_key)
      }
    }
  }

  for (const rule of def.conditional_rules) {
    for (const action of rule.actions) {
      if (!seenKeys.has(action.target_field_key)) {
        errors.push({
          rule_id: rule.rule_id,
          message: `قانون «${rule.description || rule.rule_id}» به فیلد ناموجود «${action.target_field_key}» اشاره دارد`,
        })
      }
    }
  }

  return errors
}

/**
 * Builds the minimal decisions list required by the publish Edge Function.
 * Default choice is 'grandfather' (non-destructive: keeps old data as-is).
 *
 * `breaking_pending_data_check` items with affected_count = 0 are excluded:
 * they aren't actually breaking (no real data was impacted), so the Edge
 * Function must not receive decisions for them or it will bump to major.
 */
export function buildDefaultDecisions(
  classified: ClassifiedChange[],
  impactData: ImpactResult[] | null,
) {
  return classified
    .filter((c) => {
      if (c.classification === 'breaking') return true
      if (c.classification === 'breaking_pending_data_check') {
        const impact = impactData?.find(
          (d) => d.field_key === (c.item.fieldKey ?? '') && d.change_type === c.item.type,
        )
        return (impact?.affected_count ?? 0) > 0
      }
      return false
    })
    .map((c) => ({
      diffItemId:         diffItemId(c.item),
      change_type:        c.item.type,
      field_key:          c.item.fieldKey,
      choice:             'grandfather' as const,
      removed_option_key: (c.item.details?.optionKey as string | undefined),
      old_type:           (c.item.before as string | undefined),
      new_type:           (c.item.after as string | undefined),
    }))
}
